import { HttpStatusCode } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import JobListMessages from 'src/pages/ProjectDetailsPage/components/JobList/messages';
import errorModalMessages from 'src/components/ErrorModal/messages';
import {
  ErrorType,
  useMessageHandler,
} from 'src/utils/hooks/useMessageHandler';
import { getImportProjectJobsProgress } from './api/queries/useGetImportProjectJobsProgress';
import {
  getImportProjectJobsResult,
  type ImportProjectJobsResultPayload,
} from './api/queries/useGetImportProjectJobsResult';

import {
  useDownloadImportProjectJobsResultFile,
  useGetImportProjectJobsProgress,
  useImportProjectJobs,
} from './api';

export type LastImportReportSeverity = 'error' | 'warning';

const PROGRESS_POLL_INTERVAL_MS = 1000;
const SUCCESS_MESSAGE_DURATION = 1000;
const RESULT_POLLING_PROGRESS_PERCENT = 100;

const getErrorStatus = (error: unknown) => {
  return (error as ErrorType | undefined)?.response?.status;
};

const getErrorDetails = (error: unknown) => {
  const responseData = (error as ErrorType | undefined)?.response?.data;
  const responseErrors = responseData?.errors;

  if (responseData?.error?.details) {
    return responseData.error.details;
  }

  if (Array.isArray(responseErrors) && responseErrors.length) {
    return responseErrors.map((item) => item.detail || item.details).join(' ');
  }

  return '';
};

const normalizeIssueGroup = (
  value?: Record<string, string[]>,
): Record<string, string[]> | undefined => {
  if (!value) {
    return undefined;
  }

  const normalizedEntries = Object.entries(value).reduce<
    Record<string, string[]>
  >((acc, [key, messages]) => {
    const normalizedKey = key in errorModalMessages ? key : 'other';

    if (!messages.length) {
      return acc;
    }

    acc[normalizedKey] = [...(acc[normalizedKey] ?? []), ...messages];
    return acc;
  }, {});

  return Object.keys(normalizedEntries).length ? normalizedEntries : undefined;
};

export const normalizeImportResultPayload = (
  payload?: ImportProjectJobsResultPayload,
): {
  errors?: Record<string, string[]>;
  warnings?: Record<string, string[]>;
} => {
  return {
    errors: normalizeIssueGroup(payload?.errors),
    warnings: normalizeIssueGroup(payload?.warnings),
  };
};

const getLastImportReportSeverity = (
  payload?: ImportProjectJobsResultPayload,
): LastImportReportSeverity | null => {
  const normalizedData = normalizeImportResultPayload(payload);
  const hasErrors = Boolean(
    normalizedData.errors && Object.keys(normalizedData.errors).length,
  );
  const hasWarnings = Boolean(
    normalizedData.warnings && Object.keys(normalizedData.warnings).length,
  );

  if (hasErrors) {
    return 'error';
  }

  if (hasWarnings) {
    return 'warning';
  }

  return null;
};

export const toImportModalError = (
  data: ImportProjectJobsResultPayload,
  message: string,
  status: HttpStatusCode = HttpStatusCode.UnprocessableEntity,
) => {
  const normalizedData = normalizeImportResultPayload(data);

  return {
    message,
    status,
    response: {
      status,
      data: normalizedData,
    },
  } as ErrorType;
};

const toImportModalErrorFromApiError = (error: ErrorType) => {
  const details = getErrorDetails(error) || error.message || 'Import failed';

  return toImportModalError(
    {
      errors: {
        other: [details],
      },
    },
    error.message || 'Import failed',
    error.response.status,
  );
};

const isWarningsOnlyResult = (payload?: ImportProjectJobsResultPayload) => {
  const normalizedData = normalizeImportResultPayload(payload);
  const hasErrors = Boolean(
    normalizedData.errors && Object.keys(normalizedData.errors).length,
  );
  const hasWarnings = Boolean(
    normalizedData.warnings && Object.keys(normalizedData.warnings).length,
  );

  return !hasErrors && hasWarnings;
};

type UseImportJobsFlowParams = {
  projectUuid: string;
};

export const useImportJobsFlow = ({ projectUuid }: UseImportJobsFlowParams) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { handleError, handleSuccess } = useMessageHandler();
  const importJobsMutation = useImportProjectJobs({ projectUuid });
  const { downloadFailedRows } =
    useDownloadImportProjectJobsResultFile(projectUuid);

  const [isProgressEnabled, setIsProgressEnabled] = useState(false);
  const [modalState, setModalState] = useState<{
    type: 'none' | 'empty' | 'error';
    error?: ErrorType;
    canForce?: boolean;
    resultFilePath?: string | null;
  }>({ type: 'none' });
  const [lastImportReportSeverity, setLastImportReportSeverity] =
    useState<LastImportReportSeverity | null>(null);
  const lastImportedFile = useRef<File | null>(null);
  const hadImportProgress = useRef(false);
  const resultPollingTimeoutRef = useRef<number | null>(null);

  const importProgressQuery = useGetImportProjectJobsProgress(projectUuid, {
    enabled: isProgressEnabled,
    refetchInterval: PROGRESS_POLL_INTERVAL_MS,
  });

  const clearResultPollingTimeout = useCallback(() => {
    if (resultPollingTimeoutRef.current !== null) {
      window.clearTimeout(resultPollingTimeoutRef.current);
      resultPollingTimeoutRef.current = null;
    }
  }, []);

  const clearImportError = useCallback(() => {
    setModalState({ type: 'none' });
  }, []);

  const resetImportState = useCallback(() => {
    clearResultPollingTimeout();
    clearImportError();
    setIsProgressEnabled(false);
    hadImportProgress.current = false;
  }, [clearImportError, clearResultPollingTimeout]);

  const finalizeSuccessfulImport = useCallback(() => {
    handleSuccess(
      formatMessage(JobListMessages.importSuccess),
      SUCCESS_MESSAGE_DURATION,
    );
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS, projectUuid] });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.PROJECT, projectUuid],
    });
    resetImportState();
  }, [handleSuccess, projectUuid, resetImportState]);

  const failImport = useCallback(
    (error: ErrorType | Error) => {
      if ((error as ErrorType)?.response) {
        setModalState({
          type: 'error',
          error: toImportModalErrorFromApiError(error as ErrorType),
          canForce: false,
        });
        return;
      }

      handleError(error);
    },
    [handleError],
  );

  const resolveImportResult = useCallback(async () => {
    try {
      const importResult = await queryClient.fetchQuery({
        queryKey: [QUERY_KEYS.PROJECT_JOBS_IMPORT_RESULT, projectUuid],
        queryFn: () => getImportProjectJobsResult(projectUuid),
      });

      if (importResult === null) {
        resultPollingTimeoutRef.current = window.setTimeout(() => {
          void resolveImportResult();
        }, PROGRESS_POLL_INTERVAL_MS);
        return;
      }

      const resultPayload = importResult.result;
      const resultSeverity = getLastImportReportSeverity(resultPayload);

      if (importResult.status === 'failure') {
        const importReportError = toImportModalError(
          resultPayload ?? {},
          'Import failed',
        );

        resetImportState();
        setLastImportReportSeverity(resultSeverity);
        setModalState({
          type: 'error',
          error: importReportError,
          canForce:
            isWarningsOnlyResult(resultPayload) &&
            Boolean(lastImportedFile.current),
          resultFilePath: resultPayload?.result_file_path,
        });
        return;
      }

      setLastImportReportSeverity(resultSeverity);
      finalizeSuccessfulImport();
    } catch (error) {
      if (getErrorStatus(error) === HttpStatusCode.NotFound) {
        resultPollingTimeoutRef.current = window.setTimeout(() => {
          void resolveImportResult();
        }, PROGRESS_POLL_INTERVAL_MS);
        return;
      }

      resetImportState();
      failImport(error as ErrorType | Error);
    }
  }, [failImport, finalizeSuccessfulImport, projectUuid, resetImportState]);

  const executeImport = useCallback(
    (file: File, forced = false) => {
      clearImportError();
      clearResultPollingTimeout();
      setLastImportReportSeverity(null);
      setIsProgressEnabled(false);
      hadImportProgress.current = false;

      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.PROJECT_JOBS_IMPORT_PROGRESS, projectUuid],
      });
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.PROJECT_JOBS_IMPORT_RESULT, projectUuid],
      });

      importJobsMutation.mutate(
        { file, forced },
        {
          onSuccess: () => {
            setIsProgressEnabled(true);
          },
        },
      );
    },
    [clearImportError, clearResultPollingTimeout, projectUuid],
  );

  const startImport = useCallback(
    (file: File) => {
      lastImportedFile.current = file;
      executeImport(file);
    },
    [executeImport],
  );

  useEffect(() => {
    const resumeImport = async () => {
      try {
        const progress = await queryClient.fetchQuery({
          queryKey: [QUERY_KEYS.PROJECT_JOBS_IMPORT_PROGRESS, projectUuid],
          queryFn: () => getImportProjectJobsProgress(projectUuid),
        });

        if (progress.state === null) {
          return;
        }

        hadImportProgress.current = true;
        setIsProgressEnabled(true);
      } catch {
        // do nothing?
      }
    };

    void resumeImport();
  }, [projectUuid]);

  useEffect(() => {
    if (!isProgressEnabled) {
      return;
    }

    if (importProgressQuery.isError) {
      resetImportState();
      failImport(importProgressQuery.error as ErrorType | Error);
      return;
    }

    if (importProgressQuery.data?.state !== null) {
      hadImportProgress.current = true;
      return;
    }

    if (!hadImportProgress.current) {
      return;
    }

    void resolveImportResult();
  }, [
    importProgressQuery.data?.state,
    importProgressQuery.error,
    importProgressQuery.isError,
    isProgressEnabled,
  ]);

  useEffect(() => {
    return () => {
      clearResultPollingTimeout();
    };
  }, []);

  const isImportingJobs = importJobsMutation.isPending || isProgressEnabled;
  const importProgressPercent = isImportingJobs
    ? importJobsMutation.isPending
      ? 0
      : (importProgressQuery.data?.state?.percent ??
        (hadImportProgress.current ? RESULT_POLLING_PROGRESS_PERCENT : 0))
    : null;

  const openLastImportReport = useCallback(async () => {
    try {
      const latestImportResult = await queryClient.fetchQuery({
        queryKey: [
          QUERY_KEYS.PROJECT_JOBS_IMPORT_RESULT,
          projectUuid,
          'latest-view',
        ],
        queryFn: () => getImportProjectJobsResult(projectUuid),
      });

      if (latestImportResult === null) {
        setLastImportReportSeverity(null);
        setModalState({ type: 'empty' });
        return;
      }

      const resultPayload = latestImportResult.result;
      const resultSeverity = getLastImportReportSeverity(resultPayload);

      if (latestImportResult.status === 'failure') {
        const importReportError = toImportModalError(
          resultPayload ?? {},
          'Import failed',
        );

        setLastImportReportSeverity(resultSeverity);
        setModalState({
          type: 'error',
          error: importReportError,
          canForce: false,
          resultFilePath: resultPayload?.result_file_path,
        });
        return;
      }

      setLastImportReportSeverity(resultSeverity);
      setModalState({ type: 'empty' });
    } catch (error) {
      if (getErrorStatus(error) === HttpStatusCode.NotFound) {
        setLastImportReportSeverity(null);
        setModalState({ type: 'empty' });
        return;
      }

      failImport(error as ErrorType | Error);
    }
  }, [failImport, projectUuid]);

  const confirmForceImport = useCallback(() => {
    if (!lastImportedFile.current) {
      return;
    }

    executeImport(lastImportedFile.current, true);
  }, [executeImport]);

  const isErrorState = modalState.type === 'error';
  const modalError = isErrorState ? (modalState.error ?? null) : null;
  const isForceImportAvailable = isErrorState && !!modalState.canForce;
  const isFailedRowsFileAvailable = isErrorState && !!modalState.resultFilePath;

  return {
    closeImportResultModal: clearImportError,
    confirmForceImport,
    downloadFailedRows,
    isFailedRowsFileAvailable,
    importProgressPercent,
    isImportingJobs,
    lastImportReportSeverity,
    modalError,
    isForceImportAvailable,
    isImportResultEmptyStateOpen: modalState.type === 'empty',
    openLastImportReport,
    startImport,
  };
};
